import { useState, useEffect } from "react";
import { getUsers, getAssociations, getAdminProfiles, getAssociationByUserId } from "../services/associationApi";
import { User, Profile, Association, PaginationState, AssociationFilters } from "../components/ui/types";

export const useAssociationData = (isOpen: boolean) => {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const [associationsFetched, setAssociationsFetched] = useState<boolean>(false);
  
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    hasNextPage: false,
    lastKey: null,
    isLoading: false
  });

  const [filters, setFilters] = useState<AssociationFilters>({
    limit: 10
  });
  const [searchUserId, setSearchUserId] = useState<string>("");

  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationTokens, setPaginationTokens] = useState<string[]>([]);
  const [totalAssociations, setTotalAssociations] = useState<number>(0);

  const fetchUsers = async (): Promise<void> => {
    try {
      const usersData = await getUsers();
      const usersList = Array.isArray(usersData) ? usersData : (usersData.users || []);
      setUsers(usersList);
    } catch (error) {
      setUsers([]);
    }
  };

  const fetchProfiles = async (): Promise<void> => {
    try {
      const profilesData = await getAdminProfiles(100);
      
      let profilesArray: any[] = [];
      if (Array.isArray(profilesData)) {
        profilesArray = profilesData;
      } else if (profilesData && Array.isArray(profilesData.profiles)) {
        profilesArray = profilesData.profiles;
      } else if (profilesData && Array.isArray(profilesData.data)) {
        profilesArray = profilesData.data;
      } else if (profilesData && typeof profilesData === 'object') {
        const keys = Object.keys(profilesData);
        if (keys.length > 0) {
          for (const key of keys) {
            if (Array.isArray(profilesData[key])) {
              profilesArray = profilesData[key];
              break;
            }
          }
        }
      }
      
      setProfiles(profilesArray);
    } catch (error) {
      setProfiles([]);
    }
  };

  const fetchInitialData = async (): Promise<void> => {
    if (dataFetched && users.length > 0 && profiles.length > 0) {
      return;
    }
    
    try {
      setIsLoading(true);
      await Promise.all([
        fetchUsers(),
        fetchProfiles()
      ]);
      setDataFetched(true);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssociations = async (
    page: number = 1, 
    newFilters: AssociationFilters = {},
    resetData: boolean = true
  ): Promise<void> => {
    if (associationsFetched && !resetData && page === 1 && Object.keys(newFilters).length === 0 && !searchUserId.trim()) {
      return;
    }
    
    try {
      setPagination(prev => ({ ...prev, isLoading: true }));
      
      const apiParams: any = {
        limit: filters.limit || 10
      };

      if (filters.profile_id || newFilters.profile_id) {
        apiParams.profile_id = newFilters.profile_id || filters.profile_id;
      }

      if (page > 1 && paginationTokens[page - 2]) {
        apiParams.start_key = paginationTokens[page - 2];
      }

      let data;
             if (searchUserId.trim()) {
         const association = await getAssociationByUserId(searchUserId.trim());
         if (association) {
           data = { associations: [association] };
         } else {
           data = { associations: [] };
         }
       } else {
         data = await getAssociations(apiParams);
       }
       
       if (searchUserId.trim()) {
         setAssociations(data.associations || []);
         setPaginationTokens([]);
         setTotalAssociations(data.associations?.length || 0);
         setPagination({
           currentPage: 1,
           hasNextPage: false,
           lastKey: null,
           isLoading: false
         });
         setTotalPages(1);
         setAssociationsFetched(true);
       } else {
         if (resetData || page === 1) {
           setAssociations(data.associations || []);
           setPaginationTokens([]);
           setTotalAssociations(data.associations?.length || 0);
           setAssociationsFetched(true);
         } else {
           setAssociations(data.associations || []);
           setTotalAssociations(prev => {
             const currentPageAssociations = data.associations?.length || 0;
             const baseCount = (page - 1) * (filters.limit || 10);
             return baseCount + currentPageAssociations;
           });
         }

        if (data.last_key) {
          setPaginationTokens(prev => {
            const newTokens = [...prev];
            newTokens[page - 1] = data.last_key;
            return newTokens;
          });
        }

        const hasMorePages = !!data.last_key;
        
        setPagination({
          currentPage: page,
          hasNextPage: hasMorePages,
          lastKey: data.last_key ?? null,
          isLoading: false
        });

        if (hasMorePages) {
          setTotalPages(page + 1);
        } else {
          setTotalPages(Math.max(page, 1));
        }

        if ((data.associations?.length || 0) === 0 && page > 1) {
          setTotalPages(page - 1);
        }
      }

    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setAssociations([]);
    }
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage === pagination.currentPage || pagination.isLoading) return;
    
    if (newPage > totalPages && totalPages > 0) {
      return;
    }
    
    if (newPage > 1 && !paginationTokens[newPage - 2]) {
      return;
    }
    
    try {
      await fetchAssociations(newPage, filters, true);
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setTotalPages(1);
      setPaginationTokens([]);
    }
  };

  const handleFilterChange = async (newFilters: AssociationFilters) => {
    try {
      setFilters(prev => ({ ...prev, ...newFilters }));
      
      setPagination(prev => ({ 
        ...prev, 
        currentPage: 1, 
        lastEvaluatedKey: null,
        hasNextPage: false 
      }));
      
      setPaginationTokens([]);
      setTotalPages(1);
      setTotalAssociations(0);
      
      await fetchAssociations(1, newFilters, true);
    } catch (error) {
    }
  };

  const refreshAssociations = async () => {
    try {
      await fetchAssociations(1, filters, true);
    } catch (error) {
    }
  };

  const clearFilters = async () => {
    const defaultFilters = { limit: 10 };
    
    setSearchUserId("");
    setFilters(defaultFilters);
    
    setPagination({
      currentPage: 1,
      hasNextPage: false,
      lastKey: null,
      isLoading: true
    });
    setPaginationTokens([]);
    setTotalPages(1);
    setTotalAssociations(0);
    
    try {
      const data = await getAssociations({ limit: 10 });
      
      setAssociations(data.associations || []);
      setTotalAssociations(data.associations?.length || 0);
      setAssociationsFetched(true);
      
      const hasMorePages = !!data.last_key;
      setPagination({
        currentPage: 1,
        hasNextPage: hasMorePages,
        lastKey: data.last_key ?? null,
        isLoading: false
      });
      
      if (hasMorePages) {
        setTotalPages(2);
        setPaginationTokens([data.last_key]);
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setAssociations([]);
      setTotalAssociations(0);
    }
  };

  const handleSearchByUser = async (userId: string) => {
    setSearchUserId(userId);
    
    setPagination({
      currentPage: 1,
      hasNextPage: false,
      lastKey: null,
      isLoading: true
    });
    setPaginationTokens([]);
    setTotalPages(1);
    setTotalAssociations(0);
    
    try {
      const association = await getAssociationByUserId(userId.trim());
      let data;
      if (association) {
        data = { associations: [association] };
      } else {
        data = { associations: [] };
      }
      
      setAssociations(data.associations || []);
      setTotalAssociations(data.associations?.length || 0);
      setPagination({
        currentPage: 1,
        hasNextPage: false,
        lastKey: null,
        isLoading: false
      });
      setTotalPages(1);
      setAssociationsFetched(true);
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setAssociations([]);
      setTotalAssociations(0);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInitialData().catch(() => {
      });
      
      fetchAssociations(1, filters, true).catch(() => {
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setAssociations([]);
      setUsers([]);
      setProfiles([]);
      setPagination({
        currentPage: 1,
        hasNextPage: false,
        lastKey: null,
        isLoading: false
      });
      setFilters({ limit: 10 });
      setSearchUserId("");
      setPaginationTokens([]);
      setTotalPages(1);
      setTotalAssociations(0);
      setDataFetched(false);
      setAssociationsFetched(false);
    }
  }, [isOpen]);

  return {
    associations,
    users,
    profiles,
    isLoading,
    pagination,
    filters,
    totalPages,
    totalAssociations,
    searchUserId,
    fetchAssociations: refreshAssociations,
    fetchInitialData,
    handlePageChange,
    handleFilterChange,
    clearFilters,
    handleSearchByUser,
    setAssociations
  };
};